// AgentLoader Component - AgentBheem ERP
// Animated robot loader based on the AgentBheem mascot
import { cn } from '@/lib/utils'

interface AgentLoaderProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  text?: string
  className?: string
}

export function AgentLoader({ size = 'md', text, className }: AgentLoaderProps) {
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
    xl: 'w-32 h-32',
  }

  const ringSizeClasses = {
    sm: 'w-14 h-14',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
    xl: 'w-44 h-44',
  }

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg',
  }

  return (
    <div className={cn('flex flex-col items-center justify-center gap-4', className)}>
      {/* Circular Loader with Logo */}
      <div className="relative flex items-center justify-center">
        {/* Outer spinning ring */}
        <div className={cn(
          'absolute rounded-full',
          'border-4 border-transparent',
          'border-t-green-400 border-r-teal-400',
          'animate-spin',
          ringSizeClasses[size]
        )} />

        {/* Second spinning ring (opposite direction) */}
        <div className={cn(
          'absolute rounded-full',
          'border-4 border-transparent',
          'border-b-green-400/50 border-l-teal-400/50',
          'animate-spin-reverse',
          ringSizeClasses[size]
        )} style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />

        {/* Pulsing glow */}
        <div className={cn(
          'absolute rounded-full',
          'bg-gradient-to-r from-green-400/20 via-teal-400/20 to-green-400/20',
          'blur-xl animate-pulse',
          ringSizeClasses[size]
        )} />

        {/* Center Logo */}
        <img
          src="/logo.png"
          alt="Loading..."
          className={cn(
            'relative object-contain z-10',
            sizeClasses[size]
          )}
        />
      </div>

      {/* Loading text */}
      {text && (
        <p className={cn(
          'text-[var(--color-text-muted)] font-medium',
          textSizeClasses[size]
        )}>
          {text}
        </p>
      )}

      {/* Loading dots */}
      <div className="flex gap-1.5">
        <span className="w-2 h-2 rounded-full bg-green-400 animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 rounded-full bg-green-400 animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  )
}

// Simple spinning logo loader
export function LogoLoader({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg', className?: string }) {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
  }

  return (
    <div className={cn('flex items-center justify-center', className)}>
      <div className="relative">
        {/* Spinning glow ring */}
        <div className={cn(
          'absolute inset-0 rounded-full',
          'bg-gradient-to-r from-green-400 via-teal-400 to-green-400',
          'animate-spin blur-sm opacity-50',
          sizeClasses[size]
        )} />

        {/* Logo */}
        <img
          src="/logo.png"
          alt="Loading..."
          className={cn(
            'relative object-contain animate-pulse',
            sizeClasses[size]
          )}
        />
      </div>
    </div>
  )
}

// Full page loader - centered in viewport
export function PageLoader({ text = 'Initializing...' }: { text?: string }) {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[var(--color-bg-base)]">
      <AgentLoader size="xl" text={text} />
    </div>
  )
}

// Viewport centered loader (for use within layouts)
export function ViewportLoader({ text = 'Loading...' }: { text?: string }) {
  return (
    <div className="h-full w-full flex items-center justify-center min-h-[400px]">
      <AgentLoader size="lg" text={text} />
    </div>
  )
}

// Inline loader for buttons/small spaces
export function InlineLoader({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-1', className)}>
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  )
}
